import { useState } from 'react'
import { Zap } from 'lucide-react'
import { Button, cn } from '@waago/ui'
import { devCodeAvailable, useAuth } from '@waago/api'

/**
 * One-click sign-in for development.
 *
 * The accounts behind these buttons are seeded by the `local` and `ci` Spring
 * profiles and exist nowhere else. Shipping a visible one-click login to
 * production would be an open door, so the guard is on the build mode rather
 * than a runtime flag someone can flip.
 *
 * <p>That guard is {@link devCodeAvailable}, shared with the OTP buttons,
 * rather than `import.meta.env.DEV` read here. The hosted demo stack builds
 * with `pnpm -r build`, which sets DEV false — so a local check passed on the
 * development machine and silently removed every quick-login button from the
 * deployed server, which is the one place they are actually needed.
 */
type DemoAccount = { name: string; detail: string; email: string }

const PASSWORD = 'waago-dev-password'

const ACCOUNTS: DemoAccount[] = [
  { name: 'Kristin Watson', detail: 'Particulier · Cotonou', email: 'kristin@example.bj' },
  { name: 'Marvin McKinney', detail: 'Particulier · Abomey-Calavi', email: 'marvin@example.bj' },
  { name: 'Mitsubishi Bénin', detail: 'Entreprise', email: 'contact@mitsubishi.bj' },
]

export default function DemoLogins({ className }: { className?: string }) {
  const { login } = useAuth()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!devCodeAvailable()) return null

  async function pick(account: DemoAccount) {
    setError(null)
    setBusy(account.email)
    try {
      await login(account.email, PASSWORD)
    } catch {
      setError('Connexion de démonstration indisponible. Le backend est-il démarré ?')
      setBusy(null)
    }
  }

  return (
    <section aria-labelledby="demo-logins-heading" className={cn('flex flex-col', className)}>
      <div className="mb-3 flex items-center gap-2">
        <span className="h-px flex-1 bg-slate-200" />
        <h2
          id="demo-logins-heading"
          className="text-2xs flex items-center gap-1.5 font-medium tracking-wide text-slate-500 uppercase"
        >
          <Zap className="size-3" aria-hidden="true" />
          Connexion rapide
        </h2>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      {error ? (
        <p role="alert" className="animate-slide-up mb-2 text-xs text-rose-700">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        {ACCOUNTS.map((account) => (
          <Button
            key={account.email}
            variant="outline"
            block
            disabled={busy !== null}
            onClick={() => void pick(account)}
            data-testid={`demo-login-${account.email}`}
            className="h-auto justify-start py-2"
          >
            <span className="flex flex-col items-start leading-tight">
              <span className="font-medium text-slate-900">
                {busy === account.email ? 'Connexion…' : account.name}
              </span>
              <span className="text-2xs text-slate-600">{account.detail}</span>
            </span>
          </Button>
        ))}
      </div>

      <p className="text-2xs mt-3 text-slate-500">
        Comptes de démonstration — disponibles uniquement en développement.
      </p>
    </section>
  )
}
